import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Hammer,
  Package,
  BellRing,
  Truck,
  Wallet,
  LogOut,
  Plus,
  Menu,
  FileText,
  ScrollText,
  UserCog,
  Boxes,
  Settings,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import DeliveryAlert from "@/components/DeliveryAlert";
import PaymentAlertDialog from "@/components/PaymentAlertDialog";

const orderLinks = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/orders", label: "Orders", icon: Package },
  { to: "/deliveries", label: "Deliveries", icon: Truck },
  { to: "/payments", label: "Payments", icon: Wallet },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/karigars", label: "Karigars", icon: Hammer },
  { to: "/reminders", label: "Reminders", icon: BellRing },
];

const voucherLinks = [
  { to: "/voucher", label: "Voucher Dashboard", icon: LayoutDashboard, end: true },
  { to: "/voucher/vouchers", label: "Vouchers", icon: ScrollText },
  { to: "/voucher/items", label: "Item Master", icon: Boxes },
  { to: "/voucher/customers", label: "Customer Master", icon: Users },
  { to: "/voucher/salesmen", label: "Salesman Master", icon: UserCog },
];

const adminLinks = [
  { to: "/settings/company", label: "Company Settings", icon: Settings },
  { to: "/settings/account", label: "Account & Security", icon: Shield },
];

function LinkGroup({ title, links, onNavigate, testidPrefix }) {
  return (
    <div>
      {title && (
        <div className="overline text-zinc-400 px-3 pt-3 pb-1" data-testid={`${testidPrefix}-group-title`}>
          {title}
        </div>
      )}
      <div className="space-y-1">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onNavigate}
            className={({ isActive }) => cn("nav-link", isActive && "active")}
            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <l.icon className="w-4 h-4" strokeWidth={1.7} />
            {l.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const role = user?.role;
  const isAdmin = role === "admin";
  const showOrder = isAdmin || role === "order_staff" || role === "staff";
  const showVoucher = isAdmin || role === "voucher_staff";

  return (
    <div className="h-full flex flex-col bg-white border-r border-border">
      <div className="px-4 py-5 border-b border-border bg-white">
        <img
          src="/logo.png"
          alt="Om Jewels — Dealer of gold kadli"
          className="w-full max-h-28 object-contain"
          data-testid="sidebar-logo"
        />
      </div>

      <nav className="flex-1 px-3 py-2 space-y-2 overflow-y-auto" data-testid="sidebar-nav">
        {showOrder && (
          <LinkGroup title="Order ERP" links={orderLinks} onNavigate={onNavigate} testidPrefix="order" />
        )}
        {showVoucher && (
          <LinkGroup title="Voucher ERP" links={voucherLinks} onNavigate={onNavigate} testidPrefix="voucher" />
        )}
        {isAdmin && (
          <LinkGroup title="Administration" links={adminLinks} onNavigate={onNavigate} testidPrefix="admin" />
        )}
      </nav>

      <div className="px-3 py-4 border-t border-border space-y-2">
        <div className="px-3 py-2">
          <div className="text-xs font-semibold text-zinc-900 truncate" data-testid="sidebar-user-name">
            {user?.name}
          </div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          <div className="overline text-gold-600 mt-1">{user?.role}</div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-zinc-600 hover:text-zinc-900"
          onClick={logout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const role = user?.role;
  const isVoucherOnly = role === "voucher_staff";

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile top bar */}
        <header
          className="lg:hidden sticky top-0 z-20 bg-white border-b border-border px-4 flex items-center justify-between"
          style={{
            paddingTop: "max(0.75rem, env(safe-area-inset-top))",
            paddingBottom: "0.75rem",
          }}
        >
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[85vw] max-w-xs overflow-y-auto">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="font-heading font-black tracking-tight flex items-center gap-2 min-w-0 flex-1 justify-center">
            <img src="/logo.png" alt="Om Jewels" className="h-8 w-auto object-contain" />
          </div>
          <Button
            size="sm"
            className="bg-zinc-900 text-white hover:bg-zinc-800 shrink-0"
            onClick={() => navigate(isVoucherOnly ? "/voucher/vouchers/new" : "/orders/new")}
            data-testid="mobile-new-order"
          >
            <Plus className="w-4 h-4 mr-1" /> {isVoucherOnly ? "Voucher" : "Order"}
          </Button>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1400px] w-full route-enter overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      {!isVoucherOnly && <DeliveryAlert />}
      <PaymentAlertDialog />
    </div>
  );
}
